/**
 * Diagnostic quiz modal — lazy-loads Typeform's embed script on first interaction.
 *
 * The Typeform inline embed (`data-tf-live`) lives inside an <x-modal> rendered globally from
 * snippets/diagnostic-quiz-modal.liquid. To avoid shipping ~50 KB of third-party JS on every
 * page-load (LCP/INP impact), we wait until the user clicks any element with
 * aria-controls="modal-diagnostic-quiz" and only then inject embed.typeform.com/next/embed.js.
 *
 * We also intercept any <a href="*typeform.com*"> on the page — merchants often paste a raw
 * Typeform URL into editorial rich-text spots (announcement bar, banners, menu links) where
 * the button.liquid auto-conversion does not run. We swap the navigation for opening the modal.
 */
class DiagnosticQuizLoader extends HTMLElement {
  static EMBED_SCRIPT_SRC = 'https://embed.typeform.com/next/embed.js';
  static TARGET_MODAL_ID = 'modal-diagnostic-quiz';
  static TRIGGER_SELECTOR = `[aria-controls="${DiagnosticQuizLoader.TARGET_MODAL_ID}"]`;
  static TYPEFORM_LINK_SELECTOR = 'a[href*="typeform.com"]';

    connectedCallback () {
    this._loaded = false;

    // On the dedicated /pages/diagnostic the embed is the page's main content: there is no
    // trigger to wait for and no modal to open, so load the script on connect and skip the
    // click wiring entirely.
    if (this.hasAttribute('eager')) {
      this._loadEmbedScript();
      return;
    }

    this._abort = new AbortController();

    // Capture phase so we run before the modal's own click handler — gives the script
    // a head start on loading while the modal is animating open, and lets us preventDefault()
    // on Typeform anchor links before the browser starts navigating.
    document.addEventListener('click', (event) => this._handleClick(event), {
      signal: this._abort.signal,
      capture: true
    });
  }

  disconnectedCallback () {
    this._abort?.abort();
  }

  _handleClick (event) {
    if (event.target.closest(DiagnosticQuizLoader.TRIGGER_SELECTOR)) {
      this._loadEmbedScript();
      return;
    }

    const typeformLink = event.target.closest(DiagnosticQuizLoader.TYPEFORM_LINK_SELECTOR);
    if (!typeformLink) return;

    event.preventDefault();
    this._loadEmbedScript();
    this._openModal();
  }

  _openModal () {
    const modal = document.getElementById(DiagnosticQuizLoader.TARGET_MODAL_ID);
    if (modal && typeof modal.show === 'function') modal.show();
  }

  _loadEmbedScript () {
    if (this._loaded) return;
    this._loaded = true;

    // Defensive: another component on the page may have already injected the script.
    if (document.querySelector(`script[src="${DiagnosticQuizLoader.EMBED_SCRIPT_SRC}"]`)) return;

    const script = document.createElement('script');
    script.src = DiagnosticQuizLoader.EMBED_SCRIPT_SRC;
    script.async = true;
    document.head.appendChild(script);
  }
}

if (!customElements.get('diagnostic-quiz-loader')) {
  customElements.define('diagnostic-quiz-loader', DiagnosticQuizLoader);
}
